import { ARTICLES } from '@/mocks/articles';

type Body = (typeof ARTICLES)[number]['body'];
type Section = Body[number];

export default function ArticleBody({ body }: { body: Body }) {
  return (
    <article className="max-w-none">
      {body.map((section: Section, index) => {
        const key = `${section.type}-${index}`;
        switch (section.type) {
          case 'lead':
            return (
              <p
                key={key}
                className="text-base md:text-lg text-foreground-800 leading-loose border-l-2 border-primary-500 pl-5 md:pl-6"
              >
                {section.text}
              </p>
            );
          case 'heading':
            return (
              <h2
                key={key}
                id={`section-${index}`}
                className="mt-12 mb-5 font-heading text-xl md:text-2xl text-foreground-950 scroll-mt-28"
              >
                {section.text}
              </h2>
            );
          case 'paragraph':
            return (
              <p key={key} className="mt-5 text-sm md:text-base text-foreground-600 leading-loose">
                {section.text}
              </p>
            );
          case 'list':
            return (
              <div key={key} className="mt-6">
                {section.text && (
                  <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
                    {section.text}
                  </p>
                )}
                <ul className="mt-4 space-y-3">
                  {section.items.map((item) => (
                    <li
                      key={item}
                      className="flex gap-3 text-sm md:text-base text-foreground-600 leading-relaxed"
                    >
                      <span className="mt-2 w-1.5 h-1.5 shrink-0 rounded-full bg-accent-500"></span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          case 'quote':
            return (
              <blockquote
                key={key}
                className="mt-8 rounded-lg bg-background-100 border-l-2 border-accent-500 px-6 py-6"
              >
                <p className="font-serif-sc text-base md:text-lg text-foreground-800 leading-loose">
                  {section.text}
                </p>
                {section.caption && (
                  <footer className="mt-3 text-xs text-foreground-600">{section.caption}</footer>
                )}
              </blockquote>
            );
          case 'image':
            return (
              <figure key={key} className="mt-8">
                <div className="w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-200">
                  <img
                    src={section.image}
                    alt={section.caption || '档案复原示意图'}
                    title="黑料百科档案复原图"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {section.caption && (
                  <figcaption className="mt-3 text-xs text-foreground-600">
                    {section.caption}
                  </figcaption>
                )}
              </figure>
            );
          case 'note':
            return (
              <aside
                key={key}
                className="mt-10 rounded-lg border border-dashed border-primary-500/40 bg-primary-500/5 px-6 py-5"
              >
                <div className="flex items-center gap-2 text-xs tracking-widest text-primary-500 mb-2">
                  <i className="ri-information-line"></i>
                  档案说明
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed">{section.text}</p>
              </aside>
            );
          default:
            return null;
        }
      })}
    </article>
  );
}